import { initializeApp } from 'firebase/app';
import {
  getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged,
  EmailAuthProvider, reauthenticateWithCredential, updatePassword, sendPasswordResetEmail,
} from 'firebase/auth';
import {
  getFirestore, collection, doc, setDoc, updateDoc, deleteDoc,
  query, where, onSnapshot, serverTimestamp, getDoc,
  arrayUnion, arrayRemove,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyCoZam77EfvxWiyu1R2IEaQjQ4Tl19VAlc",
  authDomain: "notepad-56a6a.firebaseapp.com",
  projectId: "notepad-56a6a",
  storageBucket: "notepad-56a6a.firebasestorage.app",
  messagingSenderId: "667137513139",
  appId: "1:667137513139:web:f1a212540ffda072a002c6",
};

const app = initializeApp(firebaseConfig);
export { app };
export const GOOGLE_MAPS_API_KEY = 'AIzaSyBrIDVK_fZFxi_XrA8THSn7IanTXk3gwiU';
export const auth = getAuth(app);
export const db = getFirestore(app);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const registerUser = (email, password) => createUserWithEmailAndPassword(auth, email, password);
export const signInUser = (email, password) => signInWithEmailAndPassword(auth, email, password);
export const signOutUser = () => signOut(auth);
export const onAuthStateChange = (cb) => onAuthStateChanged(auth, cb);
export const sendPasswordReset = (email) => sendPasswordResetEmail(auth, email);
export const changeUserPassword = async (user, currentPassword, newPassword) => {
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, currentPassword ? credential : null);
  await updatePassword(user, newPassword);
};
export const reauthUser = (user, currentPassword) => {
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  return reauthenticateWithCredential(user, credential);
};

// ── Helpers ───────────────────────────────────────────────────────────────────
// Firestore rejects `undefined` values. Recursively strip them (plain objects
// only — Firestore special types like FieldValue/Timestamp are left intact).
function stripUndefined(val) {
  if (val === undefined) return null;
  if (val === null || typeof val !== 'object') return val;
  if (Array.isArray(val)) return val.map(stripUndefined);
  if (val.constructor !== Object) return val; // preserve FieldValue, Timestamp, etc.
  const out = {};
  for (const [k, v] of Object.entries(val)) {
    out[k] = stripUndefined(v);
  }
  return out;
}

// ── Trips ─────────────────────────────────────────────────────────────────────
export const tripsCollection = collection(db, 'trips');

export const getUserTrips = (userId, callback) => {
  const q = query(tripsCollection, where('userId', '==', userId));
  return onSnapshot(q, (snap) => {
    const trips = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    trips.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
    callback(trips);
  }, () => callback([]));
};

export const createTrip = async (userId, tripData) => {
  const ref = doc(tripsCollection);
  await setDoc(ref, stripUndefined({ userId, ...tripData, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
  return ref.id;
};

export const updateTrip = async (tripId, updates) => {
  await updateDoc(doc(tripsCollection, tripId), stripUndefined({ ...updates, updatedAt: serverTimestamp() }));
};

export const deleteTrip = async (tripId) => {
  await deleteDoc(doc(tripsCollection, tripId));
};

export const getTripOnce = async (tripId) => {
  const snap = await getDoc(doc(tripsCollection, tripId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const subscribeTrip = (tripId, callback) => {
  return onSnapshot(doc(tripsCollection, tripId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
};

// ── User Prefs ────────────────────────────────────────────────────────────────
const userPrefsCollection = collection(db, 'userPrefs');

export const getUserPrefs = (userId, callback) => {
  return onSnapshot(doc(userPrefsCollection, userId), (snap) => {
    callback(snap.exists() ? snap.data() : null);
  });
};

export const saveUserPrefs = async (userId, prefs) => {
  await setDoc(doc(userPrefsCollection, userId), stripUndefined({ ...prefs, userId, updatedAt: serverTimestamp() }), { merge: true });
};

// ── Inspiration cache ─────────────────────────────────────────────────────────
const inspirationCollection = collection(db, 'tpInspiration');

export const getInspirationCache = async (category) => {
  const snap = await getDoc(doc(inspirationCollection, category));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (data.expiresAt?.toDate() < new Date()) return null; // expired
  return data;
};

export const saveInspirationCache = async (category, destinations) => {
  const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  await setDoc(doc(inspirationCollection, category), {
    category, destinations, generatedAt: serverTimestamp(), expiresAt,
  });
};

// ── Users (for friend lookup across apps) ────────────────────────────────────

export const registerUserProfile = async (uid, email) => {
  await setDoc(doc(db, 'users', uid), { email, updatedAt: serverTimestamp() }, { merge: true });
};

// ── Friends ───────────────────────────────────────────────────────────────────

export const subscribeFriendships = (email, callback) => {
  const normalised = email.toLowerCase();
  let sent = [];
  let received = [];
  let unsub1Done = false;
  let unsub2Done = false;

  const merge = () => {
    if (unsub1Done && unsub2Done) callback([...sent, ...received]);
  };

  const unsub1 = onSnapshot(
    query(collection(db, 'friendships'), where('requesterEmail', '==', normalised)),
    (snap) => {
      sent = snap.docs.map((d) => ({ id: d.id, ...d.data(), direction: 'sent' }));
      unsub1Done = true;
      merge();
    },
    () => { unsub1Done = true; merge(); }
  );

  const unsub2 = onSnapshot(
    query(collection(db, 'friendships'), where('recipientEmail', '==', normalised)),
    (snap) => {
      received = snap.docs.map((d) => ({ id: d.id, ...d.data(), direction: 'received' }));
      unsub2Done = true;
      merge();
    },
    () => { unsub2Done = true; merge(); }
  );

  return () => { unsub1(); unsub2(); };
};

// ── Trip Sharing ──────────────────────────────────────────────────────────────

export const shareTrip = async (tripId, friendUid) => {
  await updateDoc(doc(tripsCollection, tripId), { sharedWith: arrayUnion(friendUid) });
};

export const unshareTrip = async (tripId, friendUid) => {
  await updateDoc(doc(tripsCollection, tripId), { sharedWith: arrayRemove(friendUid) });
};

export const subscribeSharedTrips = (uid, callback) => {
  const q = query(tripsCollection, where('sharedWith', 'array-contains', uid));
  return onSnapshot(q, (snap) => {
    const trips = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    trips.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
    callback(trips);
  }, () => callback([]));
};
